import { takeLatest, put, call, SagaReturnType } from 'redux-saga/effects';
import { pbjExportActions } from '../../actions/constant';
import { getPbjExport } from 'src/services/pbjService/getPbjExport';
import {
  decodePbjErrorBody,
  fallbackPbjFilename,
  isPbjTimeoutError,
  parsePbjFilename,
  parsePbjWarnings,
  pbjContentTypeFor,
  readPbjHeader,
} from 'src/services/pbjService/parsePbjResponse';
import { PBJ_NO_DATA_CODE } from 'src/services/pbjService/constants';

const {
  GET_PBJ_EXPORT_REQUEST,
  GET_PBJ_EXPORT_LOADING,
  GET_PBJ_EXPORT_SUCCESS,
  GET_PBJ_EXPORT_FAILURE,
} = pbjExportActions;

/**
 * RA-2027 — `GET /pbj`.
 *
 * `takeLatest` on purpose: clicking XML then CSV should download the CSV, not race two 11-second
 * generations to see which blob lands last.
 *
 * `X-PBJ-Warning-Count` / `X-PBJ-Warnings` are read on **both** the success and the failure path.
 * On the tenant measured, `facility.pbj_number` was NULL for all 34 facilities and
 * `provider.pbj_number` was set for 24 of 620 providers — so the first download a customer takes
 * will be short and CMS-invalid, and the warning summary is the only in-band signal that explains
 * why. Dropping it on the 404 would lose it in exactly the case where it matters most.
 */
//  Return type is `Generator<any, void, any>` rather than the `SagaReturnType<any>` most sagas in
//  this repo use. `SagaReturnType<any>` resolves to `any`, which leaves a hand-stepped iterator
//  typed `unknown` and costs 7 `tsc` errors in this saga's test — and `tsconfig.json` on develop
//  type-checks test files, so that fails `npm run build:qa`.
function* getPbjExportHandler(action: any): Generator<any, void, any> {
  const request = action?.payload;

  try {
    yield put({ type: GET_PBJ_EXPORT_LOADING });

    const response = yield call(getPbjExport, request);

    if (response?.status === 200) {
      const { warningCount, warnings } = parsePbjWarnings(response.headers);

      yield put({
        type: GET_PBJ_EXPORT_SUCCESS,
        payload: {
          download: {
            data: response.data,
            //  The server's own Content-Type, so the Blob matches the bytes; the format-derived
            //  value is only a fallback for a proxy that strips it.
            contentType:
              readPbjHeader(response.headers, 'Content-Type') ?? pbjContentTypeFor(request?.format),
            //  Filename from Content-Disposition, so the server stays the source of truth for it.
            filename: parsePbjFilename(
              response.headers,
              fallbackPbjFilename(request?.year, request?.quarter, request?.format)
            ),
          },
          format: request?.format,
          warningCount,
          warnings,
        },
      });
    } else {
      throw response;
    }
  } catch (error: any) {
    const response = error?.response ?? (error?.status ? error : undefined);
    const body = decodePbjErrorBody(response?.data);
    const { warningCount, warnings } = parsePbjWarnings(response?.headers);

    yield put({
      type: GET_PBJ_EXPORT_FAILURE,
      payload: {
        status: response?.status ?? null,
        code: (body?.code as string) ?? null,
        isNoData: body?.code === PBJ_NO_DATA_CODE,
        isTimeout: isPbjTimeoutError(error),
        warningCount,
        warnings,
      },
    });
  }
}

function* getPbjExportSaga() {
  yield takeLatest(GET_PBJ_EXPORT_REQUEST, getPbjExportHandler);
}

export { getPbjExportHandler };
export default getPbjExportSaga;
